import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deleteLegendCaseFile, LEGEND_DOC_ACCEPT, uploadLegendCaseFiles, useLegendOpsDraftFields } from './legendOpsShared';
import FileDropZone from './FileDropZone';
import LegendReportChat from './LegendReportChat';
import LegendCaseFileView from './LegendCaseFileView';
import LegendParleyDebrief from './LegendParleyDebrief';

const EMPTY_FILES = [];


export default function LegendParleyWorkspace({
  sessionId,
  apiFetch,
  error,
  onError,
  savedReportHtml,
  savedReportTitle,
  savedUploadedFiles = EMPTY_FILES,
  onUploadedFilesChange,
  savedDraft,
  onDraftChange,
  onReportHtmlUpdated,
  savedReportChatMessages = [],
  onReportChatMessagesChange,
  onConversationRefresh,
}) {
  const {
    caseName,
    setCaseName,
    osintNotes,
    setOsintNotes,
    socialLinks,
    setSocialLinks,
    direction,
    setDirection,
  } = useLegendOpsDraftFields({ savedDraft, sessionId, onDraftChange });
  const [uploadedFiles, setUploadedFiles] = useState(
    Array.isArray(savedUploadedFiles) ? savedUploadedFiles : EMPTY_FILES,
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [deletingName, setDeletingName] = useState('');
  const [running, setRunning] = useState(false);
  const [readerWide, setReaderWide] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [htmlReport, setHtmlReport] = useState(savedReportHtml || null);
  const [reportPreviewKey, setReportPreviewKey] = useState(0);
  const [reportTitle, setReportTitle] = useState(savedReportTitle || '');
  const fileInputRef = useRef(null);
  const socialInputRef = useRef(null);
  const cancelledRef = useRef(false);
  const caseFiles = uploadedFiles.filter((file) => (file.upload_kind || 'knowledge') !== 'social_media');
  const socialFiles = uploadedFiles.filter((file) => file.upload_kind === 'social_media');

  useEffect(() => {
    setUploadedFiles(Array.isArray(savedUploadedFiles) ? savedUploadedFiles : EMPTY_FILES);
  }, [savedUploadedFiles, sessionId]);

  useEffect(() => {
    setHtmlReport(savedReportHtml || null);
    setReportTitle(savedReportTitle || '');
  }, [savedReportHtml, savedReportTitle, sessionId]);

  useEffect(() => {
    setReaderWide(false);
  }, [sessionId]);

  const pollStatus = useCallback(async () => {
    const params = new URLSearchParams({ session_id: sessionId, _t: String(Date.now()) });
    const response = await apiFetch(`/api/reports/generate/status?${params}`, { cache: 'no-store' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail?.message || errorData.detail || `Status check failed (${response.status})`);
    }
    return response.json();
  }, [apiFetch, sessionId]);

  const uploadFiles = async (fileList, uploadKind = 'knowledge') => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(
      uploadKind === 'social_media'
        ? `Preparing social media dump${files.length === 1 ? '' : 's'}…`
        : `Preparing ${files.length} file${files.length === 1 ? '' : 's'}…`,
    );
    onError?.(null);
    try {
      const next = await uploadLegendCaseFiles({
        sessionId,
        fileList: files,
        existingFiles: uploadedFiles,
        onProgress: setUploadProgress,
        onStatus: setUploadStatus,
        uploadKind,
      });
      setUploadedFiles(next);
      onUploadedFilesChange?.(next);
    } catch (err) {
      onError?.(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const removeUploadedFile = async (file) => {
    const name = file?.filename || file?.name;
    if (!name || deletingName || uploading || running) return;
    setDeletingName(name);
    onError?.(null);
    try {
      const serverFiles = await deleteLegendCaseFile({
        sessionId,
        filename: name,
        apiFetch,
      });
      const next = Array.isArray(serverFiles)
        ? serverFiles
        : uploadedFiles.filter((item) => (item.filename || item.name) !== name);
      setUploadedFiles(next);
      onUploadedFilesChange?.(next);
    } catch (err) {
      onError?.(err.message || `Failed to delete ${name}`);
    } finally {
      setDeletingName('');
    }
  };

  const handleStart = async () => {
    const name = caseName.trim();
    if (!name) {
      onError?.('Enter a case name before generating the playbook.');
      return;
    }
    if (!uploadedFiles.length && !osintNotes.trim() && !socialLinks.trim()) {
      onError?.('Add case files, OSINT notes, or social links before generating.');
      return;
    }
    cancelledRef.current = false;
    setRunning(true);
    setProgressMessage('Starting Legend Parley…');
    setProgressPercent(5);
    onError?.(null);
    try {
      const response = await apiFetch('/api/reports/generate', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          mode: 'legend_parley',
          specialty: 'negotiation_path',
          pipeline_mode: 'legend_parley',
          report_title: name,
          parley_osint_notes: osintNotes.trim() || undefined,
          parley_social_links: socialLinks.trim() || undefined,
          parley_direction: direction.trim() || undefined,
        }),
      });
      if (!response.ok && response.status !== 202) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.message || errorData.detail || 'Failed to start Parley');
      }
      const deadline = Date.now() + 90 * 60 * 1000;
      while (Date.now() < deadline) {
        if (cancelledRef.current) break;
        const status = await pollStatus();
        if (status.message) setProgressMessage(status.message);
        if (typeof status.progress === 'number') setProgressPercent(status.progress);
        if (status.status === 'completed') {
          const html = status.report?.html_content || status.html_content;
          if (!html) throw new Error('Parley finished but no playbook HTML was returned.');
          setHtmlReport(html);
          setReportTitle(status.report?.title || name);
          onReportHtmlUpdated?.({ html, title: status.report?.title || name, report: status.report });
          setProgressMessage('Playbook ready.');
          setProgressPercent(100);
          break;
        }
        if (status.status === 'failed') {
          throw new Error(status.error || status.message || 'Parley failed');
        }
        if (status.status === 'cancelled') {
          throw new Error('Parley was cancelled.');
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    } catch (err) {
      if (!cancelledRef.current) onError?.(err.message || 'Parley failed');
    } finally {
      setRunning(false);
      cancelledRef.current = false;
    }
  };

  const handleStop = async () => {
    cancelledRef.current = true;
    setProgressMessage('Stopping…');
    try {
      await apiFetch('/api/reports/generate/cancel', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch {
      // best effort
    }
  };

  const downloadHtml = () => {
    if (!htmlReport) return;
    const blob = new Blob([htmlReport], { type: 'text/html;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(reportTitle || caseName || 'legend-parley').replace(/[^\w-]+/g, '_')}.html`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="legend-parley-workspace flex-1 min-h-0 overflow-auto">
      <section className="legend-parley-hero px-4 pt-5 pb-4">
        <img
          src="/branding/legend-parley-logo.png"
          alt="Legend Parley"
          className="legend-parley-hero-logo"
        />
        <p className="legend-parley-lede">
          Feed the deal, counterpart OSINT, and public footprint. Get a clear path to winning the negotiation.
        </p>
      </section>

      <div className={`container mx-auto px-4 pb-10 ${
        readerWide
          ? 'space-y-4'
          : 'grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]'
      }`}>
        <div className={`space-y-5 ${readerWide ? 'hidden' : ''}`}>
          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Case name</span>
            <input
              value={caseName}
              onChange={(e) => setCaseName(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] focus:outline-none focus:border-[#c4a35a]"
              placeholder="e.g. Contoso supply renewal"
              disabled={running}
            />
          </label>

          <div className="space-y-2">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Case pack documents</span>
            <FileDropZone
              disabled={uploading || running}
              onFiles={(files) => uploadFiles(files, 'knowledge')}
              className="rounded-md border border-dashed px-3 py-4 border-[#3a342c] bg-[#14181d] transition-colors"
              activeClassName="border-[#c4a35a] bg-[#c4a35a]/10"
            >
              <p className="text-sm text-[#cfc6b6]">
                Drag and drop PDFs, Word, Excel, or text, or{' '}
                <button
                  type="button"
                  className="text-[#c4a35a] hover:underline disabled:opacity-40"
                  disabled={uploading || running}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? `Uploading… ${uploadProgress}%` : 'browse files'}
                </button>
              </p>
              <p className="text-xs text-[#6f675c] mt-1">
                Deal memos, term sheets, emails, dossiers. Drag and drop or browse. Multiple files accumulate. No pack size limit.
              </p>
              {uploading && uploadStatus && (
                <p className="text-xs text-[#c4a35a] mt-1 truncate">{uploadStatus}</p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={LEGEND_DOC_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  uploadFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </FileDropZone>
            <ul className="text-sm text-[#cfc6b6] space-y-1 min-h-[1.5rem]">
              {caseFiles.map((file) => {
                const name = file.filename || file.name;
                return (
                  <li key={name} className="flex items-center gap-2 min-w-0">
                    <span className="truncate flex-1">{name}</span>
                    <button
                      type="button"
                      className="shrink-0 text-[11px] text-[#6f675c] hover:text-red-300 disabled:opacity-40"
                      disabled={uploading || running || deletingName === name}
                      onClick={() => removeUploadedFile(file)}
                      title={`Remove ${name}`}
                      aria-label={`Remove ${name}`}
                    >
                      {deletingName === name ? 'Removing…' : 'Remove'}
                    </button>
                  </li>
                );
              })}
              {!caseFiles.length && (
                <li className="text-[#6f675c]">No documents uploaded yet (OSINT-only is fine).</li>
              )}
            </ul>
          </div>

          <div className="space-y-2">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Social media dump (optional)</span>
            <p className="text-xs text-[#6f675c]">
              Upload posts, messages, or exports (CSV, Excel, PDF, TXT, JSON, ZIP). DISC is scored from this dump when you generate the playbook: rank, proofs, and how to talk with the counterpart.
            </p>
            <FileDropZone
              disabled={uploading || running}
              onFiles={(files) => uploadFiles(files, 'social_media')}
              className="rounded-md border border-dashed px-3 py-3 border-[#3a342c] bg-[#14181d] transition-colors"
              activeClassName="border-[#c4a35a] bg-[#c4a35a]/10"
            >
              <button
                type="button"
                className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] hover:border-[#c4a35a]/60 disabled:opacity-40"
                disabled={uploading || running}
                onClick={() => socialInputRef.current?.click()}
              >
                {uploading && uploadStatus.toLowerCase().includes('social')
                  ? `Uploading… ${uploadProgress}%`
                  : 'Social Media Upload'}
              </button>
              <p className="text-xs text-[#6f675c] mt-2 text-center">
                Drag and drop a dump here, or browse.
              </p>
              <input
                ref={socialInputRef}
                type="file"
                multiple
                accept="*/*"
                className="hidden"
                onChange={(e) => {
                  uploadFiles(e.target.files, 'social_media');
                  e.target.value = '';
                }}
              />
            </FileDropZone>
            <ul className="text-sm text-[#cfc6b6] space-y-1">
              {socialFiles.map((file) => {
                const name = file.filename || file.name;
                return (
                  <li key={name} className="flex items-center gap-2 min-w-0">
                    <span className="truncate flex-1">{name}</span>
                    <button
                      type="button"
                      className="shrink-0 text-[11px] text-[#6f675c] hover:text-red-300 disabled:opacity-40"
                      disabled={uploading || running || deletingName === name}
                      onClick={() => removeUploadedFile(file)}
                      title={`Remove ${name}`}
                      aria-label={`Remove ${name}`}
                    >
                      {deletingName === name ? 'Removing…' : 'Remove'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">OSINT / notes</span>
            <textarea
              value={osintNotes}
              onChange={(e) => setOsintNotes(e.target.value)}
              rows={5}
              className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] focus:outline-none focus:border-[#c4a35a]"
              placeholder="Company/opponent research, decision-makers, pressure points…"
              disabled={running}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Social / public links</span>
            <textarea
              value={socialLinks}
              onChange={(e) => setSocialLinks(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] focus:outline-none focus:border-[#c4a35a]"
              placeholder="One URL per line"
              disabled={running}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-[#8A8070]">Direction (binding)</span>
            <textarea
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 bg-[#14181d] border border-[#3a342c] rounded-md text-[#e8e2d6] focus:outline-none focus:border-[#c4a35a]"
              placeholder="e.g. Focus only on price and liability walk-aways"
              disabled={running}
            />
          </label>

          {error && (
            <p className="text-sm text-red-300/90 border border-red-500/30 bg-red-950/30 px-3 py-2 rounded-md">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {!running ? (
              <button
                type="button"
                onClick={handleStart}
                className="px-4 py-2.5 bg-[#c4a35a] text-[#14110c] font-semibold rounded-md hover:bg-[#d4b56d]"
              >
                Generate playbook
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStop}
                className="px-4 py-2.5 border border-[#c4a35a]/60 text-[#c4a35a] rounded-md"
              >
                Stop
              </button>
            )}
            {htmlReport && (
              <button
                type="button"
                onClick={downloadHtml}
                className="px-4 py-2.5 border border-[#3a342c] text-[#e8e2d6] rounded-md hover:border-[#c4a35a]/50"
              >
                Download HTML
              </button>
            )}
          </div>

          {running && (
            <div className="space-y-1">
              <div className="h-1.5 bg-[#241f19] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#c4a35a] transition-all duration-500"
                  style={{ width: `${Math.max(progressPercent, 4)}%` }}
                />
              </div>
              <p className="text-xs text-[#9a9184]">{progressMessage}</p>
            </div>
          )}
        </div>

        <div className="space-y-3 min-w-0">
          {htmlReport && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {readerWide && (
                <button
                  type="button"
                  onClick={() => setReaderWide(false)}
                  className="px-3 py-1.5 border border-[#3a342c] text-[#e8e2d6] rounded-md text-xs hover:border-[#c4a35a]/50"
                >
                  Show case inputs
                </button>
              )}
              <button
                type="button"
                onClick={() => setReaderWide((value) => !value)}
                className="px-3 py-1.5 border border-[#3a342c] text-[#e8e2d6] rounded-md text-xs hover:border-[#c4a35a]/50"
              >
                {readerWide ? 'Narrow reader' : 'Widen reader'}
              </button>
            </div>
          )}
          <LegendCaseFileView
            htmlContent={htmlReport}
            reportTitle={reportTitle || 'Legend Parley Playbook'}
            sectionMappings={[
              { id: 'situation_brief', title: 'Situation Brief' },
              { id: 'counterpart_map', title: 'Counterpart & OSINT Map' },
              ...(htmlReport && htmlReport.includes('id="disc_communication_profile"')
                ? [{ id: 'disc_communication_profile', title: 'DISC Communication Profile' }]
                : []),
              { id: 'benchmark', title: 'Benchmark' },
              { id: 'leverage_batna', title: 'Leverage, BATNA & ZOPA' },
              { id: 'winning_path', title: 'Path to Winning' },
              { id: 'meeting_plan', title: 'Meeting Plan' },
              { id: 'talk_tracks', title: 'Talk Tracks & Concessions' },
              { id: 'risks_walkaway', title: 'Risks & Walk-Away Triggers' },
              ...(htmlReport && htmlReport.includes('id="meeting_notes"')
                ? [{ id: 'meeting_notes', title: 'Meeting Notes' }]
                : []),
            ]}
            onDownloadHtml={downloadHtml}
            className={`border border-[#3a342c] rounded-md bg-[#101418] ${readerWide ? 'min-h-[80vh]' : 'min-h-[28rem]'}`}
          />
          {htmlReport && (
            <LegendParleyDebrief
              apiFetch={apiFetch}
              sessionId={sessionId}
              htmlReport={htmlReport}
              disabled={running}
              onHtmlUpdated={({ html, title: nextTitle, report }) => {
                setHtmlReport(html);
                setReportPreviewKey((key) => key + 1);
                if (nextTitle) setReportTitle(nextTitle);
                onReportHtmlUpdated?.({ html, title: nextTitle, report });
              }}
              onError={onError}
            />
          )}
          {htmlReport && (
            <LegendReportChat
              apiFetch={apiFetch}
              sessionId={sessionId}
              reportLanguage="en"
              disabled={running}
              savedMessages={savedReportChatMessages}
              onMessagesChange={onReportChatMessagesChange}
              onConversationRefresh={onConversationRefresh}
              onHtmlUpdated={({ html, title: nextTitle }) => {
                setHtmlReport(html);
                setReportPreviewKey((key) => key + 1);
                if (nextTitle) setReportTitle(nextTitle);
                onReportHtmlUpdated?.({ html, title: nextTitle });
              }}
              onError={onError}
            />
          )}
        </div>
      </div>
    </div>
  );
}
